import type Metrics from '../Metrics';
import type { TimeSeriesMetricsAdapter } from '../Metrics';
import type { PromCounterMetricName, PromDurationMetricName, PromGaugeMetricName } from '../types';
import type { ClientMetricPayload, TimeSeriesMetricLabels } from 'bc-model';

type PromTimeSeriesAdapterOptions = {
  flushDebounceTimeMs?: number;
  maxBufferSize?: number;
};

const endpointPath = '/api/client-metrics';

const promTimeSeriesAdapter = (
  metricsInstance?: Metrics,
  opts?: PromTimeSeriesAdapterOptions
): TimeSeriesMetricsAdapter => {
  /**
   * Normalize labels by converting values to strings, truncating long values,
   * and replacing invalid characters in keys.
   * @param labels
   */
  const normalizeLabels = (labels: TimeSeriesMetricLabels) => {
    const returnVal = { method: '', path: '', type: '' };

    for (const [k, v] of Object.entries(labels)) {
      if (v === undefined || v === null) continue;
      let strVal = String(v);

      // Truncate label values that are too long and sanitize them
      if (strVal.length > 120) {
        strVal = strVal.slice(0, 120);
      }
      returnVal[k.replace(/[^\w:]/g, '_')] = strVal;
    }
    return returnVal;
  };

  const FLUSH_DEBOUNCE_TIME_MS = opts?.flushDebounceTimeMs ?? 300;
  const MAX_BUFFER_SIZE = opts?.maxBufferSize ?? 500;

  const buffer: ClientMetricPayload[] = [];
  let flushTimeoutId: number | null = null;
  const isInBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

  /**
   * Flush the buffered metrics to the server.
   */
  const flushBuffer = async () => {
    if (buffer.length === 0) return;

    if (flushTimeoutId != null) {
      clearTimeout(flushTimeoutId);
      flushTimeoutId = null;
    }
    const body = JSON.stringify({ metrics: buffer.splice(0, buffer.length) });

    try {
      await fetch(endpointPath, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      });
    } catch (e) {
      metricsInstance?.logError(
        new Error('promTimeSeriesAdapter: flushBuffer failed when sending request.'),
        {
          error: e,
        }
      );
    }
  };

  /**
   * Ensure a flush timer is set to flush the buffer after the debounce period.
   */
  const ensureFlushTimeout = () => {
    if (!isInBrowser || flushTimeoutId != null) return;

    flushTimeoutId = window.setTimeout(() => {
      void flushBuffer();
    }, FLUSH_DEBOUNCE_TIME_MS);
  };

  /**
   * Enqueue metrics to be sent to the server.
   * @param metrics
   */
  const enqueueMetrics = (metrics: ClientMetricPayload) => {
    if (!isInBrowser) return;
    buffer.push(metrics);

    // Debounce flushes - avoid excessive network calls
    if (flushTimeoutId != null) {
      clearTimeout(flushTimeoutId);
      flushTimeoutId = null;
    }

    // If we reached the normal batch size, flush promptly
    if (buffer.length >= MAX_BUFFER_SIZE) {
      void flushBuffer();
      return;
    }

    ensureFlushTimeout();
  };

  /**
   * Increment a counter metric with normalized labels.
   * @param name Metric name
   * @param labels Metric labels
   * @param value Increment amount (defaults to 1)
   */
  const incrementCounter = (
    name: PromCounterMetricName,
    labels: TimeSeriesMetricLabels,
    value?: number
  ): void => {
    enqueueMetrics({
      type: 'counter',
      name: String(name),
      value: value ?? 1,
      labels: normalizeLabels(labels),
    });
  };

  /**
   * Set a gauge metric to the provided value with normalized labels.
   * @param name Metric name
   * @param labels Metric labels
   * @param value Gauge value
   */
  const setGauge = (
    name: PromGaugeMetricName,
    labels: TimeSeriesMetricLabels,
    value: number
  ): void => {
    enqueueMetrics({
      type: 'gauge',
      name: String(name),
      value,
      labels: normalizeLabels(labels),
    });
  };

  /**
   * Record a duration metric using the provided start time and duration.
   * @param name Metric name
   * @param labels Metric labels
   */
  const observeTime = (
    name: PromDurationMetricName,
    labels: TimeSeriesMetricLabels
  ): ((start: number, durationMs: number) => void) => {
    return (start: number, durationMs: number): void => {
      enqueueMetrics({
        type: 'duration',
        name: String(name),
        value: Number((durationMs / 1000).toFixed(6)), // seconds
        startMs: start,
        endMs: start + Math.round(durationMs),
        labels: normalizeLabels(labels),
      });
    };
  };

  return { incrementCounter, setGauge, observeTime, flushBuffer };
};

export default promTimeSeriesAdapter;
